import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, App, Button, Card, Form, List, Modal, Popconfirm, Select, Space, Tag, Typography } from 'antd';
import type { FormInstance } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DisconnectOutlined,
  LinkOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IssuerAsset,
  IssuerAssetListItem,
  IssuerKey,
  RwaActivationChecklist,
  RwaHiddenSection,
  canonicalSectionLocale,
  resolveHiddenSections,
} from '@mygate/common';
import { issuersService } from '@/services/issuers';
import { IssuerAssetPreview } from '@/components/IssuerAssetPreview';

const { Text } = Typography;

interface RwaIssuerContentCardProps {
  assetId: string;
  issuerAsset: IssuerAsset | null;
  checklist: RwaActivationChecklist | undefined;
  /** The asset form — hero image and hidden sections are form fields (`rwa.*`) saved with the asset. */
  form: FormInstance;
  /** Called after a link/unlink so the parent reloads the asset (checklist included). */
  onChanged: () => void;
}

function ChecklistView({ checklist }: { checklist: RwaActivationChecklist }) {
  return (
    <List
      size="small"
      header={
        checklist.canActivate ? <Tag color="green">Ready to activate</Tag> : <Tag color="red">Activation blocked</Tag>
      }
      dataSource={checklist.items}
      renderItem={item => (
        <List.Item>
          <Space align="start">
            {item.ok ? (
              <CheckCircleFilled style={{ color: '#52c41a' }} />
            ) : item.blocking ? (
              <CloseCircleFilled style={{ color: '#ff4d4f' }} />
            ) : (
              <WarningFilled style={{ color: '#faad14' }} />
            )}
            <Space direction="vertical" size={0}>
              <Text>{item.label}</Text>
              {item.detail && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {item.detail}
                </Text>
              )}
            </Space>
          </Space>
        </List.Item>
      )}
    />
  );
}

/** Unlinked issuer records across every issuer, for the "link" picker on the asset page. */
function useUnlinkedIssuerAssets(enabled: boolean) {
  return useQuery({
    queryKey: ['issuer-assets', 'unlinked-all'],
    enabled,
    queryFn: async () => {
      const issuers = (await issuersService.listIssuers()).data ?? [];
      const lists = await Promise.all(
        issuers.map(async i => (await issuersService.listIssuerAssets(i.issuer, { linked: false })).data?.assets ?? []),
      );
      return lists.flat();
    },
  });
}

/** The "Issuer content" card on an RWA asset: what it is linked to, what we override, what blocks activation. */
export function RwaIssuerContentCard({ assetId, issuerAsset, checklist, form, onChanged }: RwaIssuerContentCardProps) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const { data: unlinked } = useUnlinkedIssuerAssets(pickerOpen);

  const heroImageUrl = Form.useWatch(['rwa', 'heroImageUrl'], form) as string | undefined;
  const hiddenSections = (Form.useWatch(['rwa', 'hiddenSections'], form) as RwaHiddenSection[] | undefined) ?? [];
  // Resolved rather than read raw, so a block the issuer moved shows as hidden at its new position —
  // the same resolution the sync and the app use.
  const hiddenIndexes = issuerAsset ? resolveHiddenSections(issuerAsset.sections, hiddenSections).hiddenIndexes : [];
  const sectionsWarning = checklist?.items.find(item => item.key === 'sections' && !item.ok);

  /**
   * Reads through the form rather than the `useWatch` snapshot: `setFieldValue` does not refresh a
   * watched value until the next render, so two toggles in one pass would both start from the same
   * array and the second would undo the first.
   */
  const toggleHidden = (index: number, hidden: boolean): void => {
    if (!issuerAsset) return;
    const current = (form.getFieldValue(['rwa', 'hiddenSections']) as RwaHiddenSection[] | undefined) ?? [];
    const without = current.filter(entry => entry.index !== index);
    if (!hidden) {
      form.setFieldValue(['rwa', 'hiddenSections'], without);
      return;
    }
    const locale = canonicalSectionLocale(issuerAsset.sections);
    const canonicalTitle = locale ? issuerAsset.sections[locale]?.[index]?.title : undefined;
    if (!canonicalTitle) return;
    form.setFieldValue(
      ['rwa', 'hiddenSections'],
      [...without, { index, canonicalTitle }].sort((left, right) => left.index - right.index),
    );
  };

  /** The Issuers page and the catalog list both show link state; neither is on this component's tree. */
  const invalidateIssuerQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['issuers'] });
    queryClient.invalidateQueries({ queryKey: ['issuer-assets'] });
  };

  const link = async () => {
    const target = (unlinked ?? []).find((a: IssuerAssetListItem) => a._id === picked);
    if (!target) return;
    setBusy(true);
    try {
      await issuersService.linkAsset(target.issuer as IssuerKey, target._id, assetId);
      message.success(`Linked to ${target.name}`);
      setPickerOpen(false);
      setPicked(undefined);
      invalidateIssuerQueries();
      onChanged();
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to link');
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!issuerAsset) return;
    setBusy(true);
    try {
      await issuersService.unlinkAsset(issuerAsset.issuer, issuerAsset._id);
      message.success('Unlinked');
      invalidateIssuerQueries();
      onChanged();
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to unlink');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Issuer content"
      style={{ marginTop: 16 }}
      extra={
        issuerAsset ? (
          <Space>
            <Button size="small" onClick={() => navigate(`/issuers/${issuerAsset.issuer}`)}>
              Open {issuerAsset.issuer} catalog
            </Button>
            <Popconfirm title="Unlink this issuer content?" onConfirm={unlink}>
              <Button size="small" danger icon={<DisconnectOutlined />} loading={busy}>
                Unlink
              </Button>
            </Popconfirm>
          </Space>
        ) : (
          <Button size="small" type="primary" icon={<LinkOutlined />} onClick={() => setPickerOpen(true)}>
            Link issuer asset…
          </Button>
        )
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {checklist && <ChecklistView checklist={checklist} />}

        {issuerAsset ? (
          <>
            <Alert
              type="info"
              showIcon
              message={
                <Space>
                  <Text strong>{issuerAsset.name}</Text>
                  <Tag>{issuerAsset.issuer}</Tag>
                  <Tag color={issuerAsset.active ? 'green' : 'red'}>
                    {issuerAsset.active ? 'published' : 'no longer published'}
                  </Tag>
                </Space>
              }
              description="Content below is the issuer's and is refreshed nightly. Pick the hero image and untick sections you do not want in the app, then save the asset."
            />
            {sectionsWarning && (
              <Alert
                type="warning"
                showIcon
                message="The issuer changed its content"
                description={`${sectionsWarning.detail ?? ''} Hiding is a presentation choice — content that must not be shown at all is a reason to deactivate the asset instead.`}
              />
            )}
            <IssuerAssetPreview
              issuerAsset={issuerAsset}
              selectedHeroUrl={heroImageUrl}
              onSelectHero={url => form.setFieldValue(['rwa', 'heroImageUrl'], url)}
              hiddenIndexes={hiddenIndexes}
              onToggleHidden={toggleHidden}
            />
          </>
        ) : (
          <Alert
            type="warning"
            showIcon
            message="No issuer content linked"
            description="Link the issuer record that describes this token (the sync links automatically once the issuer sets the token symbol)."
          />
        )}
      </Space>

      <Modal
        title="Link issuer content"
        open={pickerOpen}
        onCancel={() => setPickerOpen(false)}
        onOk={link}
        okButtonProps={{ disabled: !picked, loading: busy }}
        okText="Link"
      >
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="Choose an unlinked issuer record"
          value={picked}
          onChange={setPicked}
          optionFilterProp="label"
          options={(unlinked ?? []).map((a: IssuerAssetListItem) => ({
            value: a._id,
            label: `${a.issuer} · ${a.name}${a.tokenSymbol ? ` (${a.tokenSymbol})` : ''}`,
          }))}
          notFoundContent="No unlinked issuer records — run a sync on the Issuers page"
        />
      </Modal>
    </Card>
  );
}
